import { api } from "@/lib/api";

// Audio and text remain transient; no local storage or server history.
export function createDeskAudio({ audio, encoded, onState, onSoundBlocked }) {
  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true; audio.pause(); audio.removeAttribute("src"); audio.srcObject = null;
    audio.onended = null; audio.onerror = null;
  };
  const play = () => {
    if (closed) return;
    onState("speaking");
    Promise.resolve(audio.play()).catch(() => { if (!closed) onSoundBlocked(); });
  };
  audio.srcObject = null; audio.src = `data:audio/mpeg;base64,${encoded}`;
  audio.onended = () => { if (!closed) { close(); onState("ended"); } };
  audio.onerror = () => { if (!closed) { close(); onState("audio-error"); } };
  play();
  return { close, play };
}

export function createDeskPhrase({ source, target, audio, onState, onResult, onSoundBlocked, onError }) {
  let closed = false, stream, recorder, timer, player, chunks = [], bytes = 0, oversized = false;
  const abort = new AbortController();
  const stopTracks = () => stream?.getTracks().forEach(track => track.stop());
  const close = () => {
    if (closed) return;
    closed = true; abort.abort(); clearTimeout(timer); stopTracks(); player?.close();
    if (recorder?.state === "recording") recorder.stop();
    chunks = [];
  };
  const fail = error => { if (!closed) { close(); onError(error); } };
  const finish = () => { if (!closed && recorder?.state === "recording") recorder.stop(); };
  const ready = (async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") throw new Error("Use Safari or Chrome with microphone access over HTTPS.");
      onState("connecting");
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      if (closed) { stopTracks(); return; }
      const mime = ["audio/webm;codecs=opus", "audio/mp4", "audio/webm"].find(type => MediaRecorder.isTypeSupported(type));
      recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recorder.ondataavailable = ({ data }) => {
        if (closed) return;
        bytes += data.size;
        if (bytes > 1024 * 1024) { oversized = true; finish(); } else chunks.push(data);
      };
      recorder.onerror = () => fail(new Error("Recording failed. Please try again."));
      recorder.onstop = async () => {
        clearTimeout(timer); stopTracks();
        if (closed) return;
        try {
          if (oversized) throw new Error("The phrase is too large. Please record a shorter sentence.");
          const blob = new Blob(chunks, { type: recorder.mimeType || mime || "audio/webm" }); chunks = [];
          onState("translating");
          const { data } = await api.post("/admin/first-line-desk/phrase", blob, {
            params: { source_language: source, target_language: target },
            headers: { "Content-Type": blob.type }, signal: abort.signal, timeout: 90000,
          });
          if (closed) return;
          if (data.source_language !== source || data.target_language !== target || typeof data.source_text !== "string" || typeof data.translated_text !== "string") throw new Error("The translation response did not match this language pair. Please try again.");
          onResult(data);
          if (data.audio_base64) {
            player = createDeskAudio({ audio, encoded: data.audio_base64, onState, onSoundBlocked });
          } else { close(); onState("ended"); }
        } catch (error) { fail(error); }
      };
      recorder.start(250); onState("recording"); timer = setTimeout(finish, 30000);
    } catch (error) { if (!closed) { close(); throw error; } }
  })();
  return { ready, close, pause: finish };
}
